import { Button, Chip, Stack } from '@mui/material';
import { useState } from 'react';

import AgencyComplementDialog from './AgencyComplementDialog';
import { euros } from './complementApi';
import ComplementManagerDialog from './ComplementManagerDialog';

/**
 * Mention « complément » sur une ligne de la liste des cartes grises
 * (spécification « CG côté agence » v1.3, §7.4 et §10.10).
 *
 * - Agence : « Accepter un complément de X € » (à accepter), « Facture en
 *   attente » ou « Virement signalé » (accepté, à régler). Un clic ouvre le
 *   complément.
 * - Skipcar : la même mention, plus le bouton « Facture complémentaire » qui
 *   ouvre la fenêtre de proposition et de suivi.
 */

const VIOLET = '#6d28d9';

export interface ComplementRowData {
  RegistrationID: number;
  RegPlate?: string;
  deal_id?: number | null;
  ComplementProposeCents?: number | string | null;
  ComplementUnpaidCents?: number | string | null;
  ComplementTransferReportedAt?: string | null;
}

export interface ComplementBadgeProps {
  cg: ComplementRowData;
  /** Vue Skipcar (god / admin) : ajoute le bouton de gestion. */
  manage?: boolean;
  onChanged?: () => void | Promise<void>;
}

const ComplementBadge = ({ cg, manage = false, onChanged }: ComplementBadgeProps) => {
  const [open, setOpen] = useState(false);
  const aAccepter = Number(cg.ComplementProposeCents ?? 0);
  const aRegler = Number(cg.ComplementUnpaidCents ?? 0);
  const regPlate = cg.RegPlate ?? '';

  const label =
    aAccepter > 0
      ? manage
        ? `Complément proposé · ${euros(aAccepter)}`
        : `Accepter un complément de ${euros(aAccepter)}`
      : aRegler > 0
        ? cg.ComplementTransferReportedAt
          ? 'Virement signalé'
          : 'Facture en attente'
        : null;

  if (!label && !manage) return null;

  return (
    <Stack spacing={0.5} alignItems="center">
      {label && (
        <Chip
          size="small"
          label={label}
          onClick={() => setOpen(true)}
          sx={{
            color: VIOLET,
            borderColor: VIOLET,
            fontWeight: 600,
            background: `${VIOLET}14`,
            maxWidth: 240,
          }}
          variant="outlined"
        />
      )}
      {manage && (
        <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
          Facture complémentaire
        </Button>
      )}
      {manage ? (
        <ComplementManagerDialog
          open={open}
          onClose={() => setOpen(false)}
          registrationId={cg.RegistrationID}
          regPlate={regPlate}
          onChanged={onChanged}
        />
      ) : (
        <AgencyComplementDialog
          open={open}
          onClose={() => setOpen(false)}
          registrationId={cg.RegistrationID}
          regPlate={regPlate}
          dealId={cg.deal_id}
          onChanged={onChanged}
        />
      )}
    </Stack>
  );
};

export default ComplementBadge;
